import { AxiosError } from 'axios';
import {
  addDraftAttachment,
  buildInspectionView,
  completeDraft,
  createDraft,
  deleteDraft,
  deleteDraftAttachment,
  getDraft,
  isLocalInspectionId,
  saveDraftBody,
  saveDraftComputer,
  saveDraftSection,
  updateDraftForm,
} from './drafts';
import { idbGet, STORES } from './idb';
import { normalizeApiPath, readRefCache } from './refCache';
import { getCachedUser, selectedAuctionOf } from './session';

// محوّل axios يجيب طلبات الفحوصات المحفوظة على الجهاز بنفس شكل ردود الخادم،
// فتعمل شاشات الفحص الحالية كما هي دون أن تعرف أن الفحص لم يُرفع بعد.
const LOCAL_INSPECTION_PATH = /^inspections\/(L[0-9]+)(?:\/(.*))?$/;

export function isLocalRequest(config) {
  const match = normalizeApiPath(config?.url).match(LOCAL_INSPECTION_PATH);
  return Boolean(match && isLocalInspectionId(match[1]));
}

function respond(config, data, status = 200) {
  return { data, status, statusText: 'OK', headers: {}, config, request: { local: true } };
}

function fail(config, status, message) {
  const response = { data: { message }, status, statusText: '', headers: {}, config };
  const error = new AxiosError(message, String(status), config, null, response);
  error.userMessage = message;
  return error;
}

function readJsonBody(config) {
  const { data } = config;
  if (!data) return {};
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return data;
}

function readFormData(config) {
  const fields = {};
  let file = null;
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.data.forEach((value, key) => {
      if (key === 'file') file = value;
      else fields[key] = value;
    });
  }
  return { file, fields };
}

async function handleInspection(config, localId, rest) {
  const method = String(config.method || 'get').toLowerCase();
  const tail = rest || '';

  // فحص رُفع وحُذف من الجهاز، وما زال أحدٌ على رابطه المحلي القديم.
  if (!(await getDraft(localId))) {
    const synced = await idbGet(STORES.kv, `synced:${localId}`).catch(() => null);
    if (synced?.server_id) {
      const error = fail(config, 410, `رُفع هذا الفحص إلى الخادم برقم ${synced.code}.`);
      error.response.data.server_id = synced.server_id;
      throw error;
    }
    throw fail(config, 404, 'الفحص غير موجود على هذا الجهاز.');
  }

  if (!tail) {
    if (method === 'get') return respond(config, await buildInspectionView(localId));
    if (method === 'put') {
      await updateDraftForm(localId, readJsonBody(config));
      return respond(config, await buildInspectionView(localId));
    }
    if (method === 'delete') {
      await deleteDraft(localId);
      return respond(config, { message: 'تم حذف الفحص من الجهاز' });
    }
  }

  if (method === 'post' && (tail === 'steering' || tail === 'engine' || tail.startsWith('sections/'))) {
    await saveDraftSection(localId, tail, readJsonBody(config).items || []);
    return respond(config, { message: 'تم الحفظ على الجهاز' });
  }

  if (method === 'post' && tail === 'body') {
    const body = readJsonBody(config);
    await saveDraftBody(localId, body.items || [], body.mark_done);
    return respond(config, { message: 'تم حفظ فحص البودي على الجهاز' });
  }

  if (method === 'post' && tail === 'computer') {
    await saveDraftComputer(localId, readJsonBody(config));
    return respond(config, { message: 'تم حفظ فحص الكمبيوتر على الجهاز' });
  }

  if (method === 'post' && tail === 'attachments') {
    const { file, fields } = readFormData(config);
    if (!file) throw fail(config, 422, 'الملف مطلوب.');
    return respond(config, await addDraftAttachment(localId, file, fields), 201);
  }

  const attachmentMatch = tail.match(/^attachments\/([^/]+)$/);
  if (method === 'delete' && attachmentMatch) {
    await deleteDraftAttachment(localId, attachmentMatch[1]);
    return respond(config, { message: 'تم حذف الصورة' });
  }

  if (method === 'patch' && tail === 'complete') {
    await completeDraft(localId);
    return respond(config, { message: 'تم إنهاء الفحص على الجهاز، ويُرفع عند المزامنة.' });
  }

  if (tail === 'report' || tail.startsWith('report/')) {
    throw fail(config, 409, 'التقرير يكون متاحًا بعد رفع الفحص إلى الخادم (المزامنة).');
  }

  throw fail(config, 404, 'هذه العملية غير متاحة لفحص محفوظ على الجهاز.');
}

export async function createLocalInspection(body) {
  const form = { ...(body || {}) };
  const clientUuid = form.client_uuid;
  delete form.client_uuid;
  delete form.auction_id;
  const user = getCachedUser();
  const auction = selectedAuctionOf(user);
  return createDraft({ form, user, auction, clientUuid });
}

export default async function localAdapter(config) {
  const path = normalizeApiPath(config.url);
  try {
    const match = path.match(LOCAL_INSPECTION_PATH);
    if (match) return await handleInspection(config, match[1], match[2]);

    if (path === 'inspections' && String(config.method).toLowerCase() === 'post') {
      const draft = await createLocalInspection(readJsonBody(config));
      return respond(config, await buildInspectionView(draft.id), 201);
    }

    throw fail(config, 404, 'هذه العملية تحتاج اتصالًا بالخادم.');
  } catch (error) {
    if (error?.isAxiosError) throw error;
    throw fail(config, error?.status || 500, error?.message || 'تعذّر الحفظ على الجهاز.');
  }
}

// يجيب طلبات البيانات المرجعية من نسخة الجهاز حين يكون الاتصال مقطوعًا.
export async function cacheAdapter(config) {
  const data = await readRefCache(normalizeApiPath(config.url));
  if (data === undefined) {
    const error = new AxiosError('لا يوجد اتصال، وهذه البيانات لم تُحفظ على الجهاز بعد.', 'ERR_NETWORK', config, null);
    error.userMessage = 'لا يوجد اتصال، وهذه البيانات لم تُحفظ على الجهاز بعد. افتح التطبيق مرة مع الاتصال.';
    throw error;
  }
  return { data, status: 200, statusText: 'OK', headers: {}, config, request: { cache: true } };
}
